import { useCallback, useRef, useState } from "react";

import { postApolloDriveStart, postApolloDriveStop, getApolloCommand } from "../utils/requestApiApollo";
import { CommandStatus } from "./useApolloCommand";

/**
 * 주행 제어 훅(설계 §7, Stage 3) — 자율주행 개시(ENTER_AUTO_MODE)/수동 전환(DISENGAGE).
 * 하향 명령은 동기 ack 가 없으므로 commandId 로 상태를 폴링해 화면에 노출한다(§4.3).
 * ⚠️ 개시는 실차를 움직인다. 안전 게이트/확인 모달은 호출 측(DriveControlPanel)에서 강제한다(§10).
 */

export type DriveAction = "start" | "stop";

const POLL_MS = 600;
const POLL_TIMEOUT_MS = 15_000;
const TERMINAL: CommandStatus[] = ["ACK", "FAILED", "EXPIRED"];

export interface ApolloDriveApi {
    send: (carId: number, action: DriveAction) => void;
    action: DriveAction | null;   // 마지막으로 보낸 명령 종류
    status: CommandStatus | "idle";
    sending: boolean;
    error: string | null;
}

export function useApolloDrive(): ApolloDriveApi {
    const [action, setAction] = useState<DriveAction | null>(null);
    const [status, setStatus] = useState<CommandStatus | "idle">("idle");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopPoll = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const send = useCallback((carId: number, act: DriveAction) => {
        if (sending) return;
        setSending(true);
        setError(null);
        setAction(act);
        stopPoll();
        (async () => {
            try {
                const data = act === "start" ? await postApolloDriveStart(carId) : await postApolloDriveStop(carId);
                if (data?.result !== "ok" || !data.commandId) {
                    setError(data?.message || (act === "start" ? "개시 요청 실패" : "정지 요청 실패"));
                    setStatus("FAILED");
                    setSending(false);
                    return;
                }
                const id = data.commandId as string;
                setStatus("PENDING");

                const started = Date.now();
                pollRef.current = setInterval(async () => {
                    try {
                        const st = await getApolloCommand(carId, id);
                        const row = st?.command;
                        if (row?.status) setStatus(row.status as CommandStatus);
                        if (row?.status && TERMINAL.includes(row.status)) {
                            if (row.status === "FAILED") setError(row.error || "실행 실패");
                            stopPoll();
                            setSending(false);
                        } else if (Date.now() - started > POLL_TIMEOUT_MS) {
                            stopPoll();
                            setSending(false);
                        }
                    } catch (e) {
                        /* 일시 오류는 타임아웃까지 폴링 지속 */
                    }
                }, POLL_MS);
            } catch (e) {
                setError(act === "start" ? "개시 중 오류가 발생했습니다." : "정지 중 오류가 발생했습니다.");
                setStatus("FAILED");
                setSending(false);
            }
        })();
    }, [sending]);

    return { send, action, status, sending, error };
}
